import Controller from '@ember/controller'
import moment from 'moment-timezone';

import {
  action,
  computed,
  get,
  set
} from '@ember/object'

import {
  task,
  timeout
} from 'ember-concurrency'

import { Promise, all } from 'rsvp';
import { later } from '@ember/runloop';

import {
  inject as iService
} from '@ember/service'

import { clearAttribute, notifyUserError } from 'beebee-base/lib/utility';
import { beebeeAjaxPost, beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';
import { debug } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import { singularize } from 'ember-inflector';
import { underscore } from '@ember/string';

import getConfig from 'beebee-base/lib/get-config';

// Cart items can nest live Ember Data records (e.g. `p.item`, `pax.level`) whose
// injected services (`store`, `notifications`, ...) form reference cycles.
// `JSON.stringify` then throws "Converting circular structure to JSON" and the
// cart silently failed to persist (Sentry EMBER-SCUOLE-ECOMMERCE-2XY). The reload
// path (`routes/application.js` normalizeCart) already reads the cart back as
// plain POJOs, so persisting a plain, cycle-free clone matches what restore
// expects. Own enumerable data is kept; functions, framework back-references and
// true ancestor cycles are dropped. Reference-equality on the ancestor path (not
// a shared seen-set) means objects reused across siblings — e.g. the same `level`
// shared by several `partecipanti` — are preserved, not mistaken for cycles.
const CART_INTERNAL_KEYS = new Set([
  'store', 'notifications', 'notify', 'session', 'intl', 'log', 'router',
  'owner', 'currentUser', 'cache', 'container', '__ember_meta__',
]);

function cartToPlain(node, ancestors) {
  if (node === null || typeof node !== 'object') {
    return node;
  }
  if (ancestors.indexOf(node) !== -1) {
    return undefined;
  }
  const nextAncestors = ancestors.concat([node]);
  if (Array.isArray(node)) {
    return node.map((item) => cartToPlain(item, nextAncestors));
  }
  const out = {};
  for (const key of Object.keys(node)) {
    if (CART_INTERNAL_KEYS.has(key)) {
      continue;
    }
    const value = cartToPlain(node[key], nextAncestors);
    if (value !== undefined && typeof value !== 'function') {
      out[key] = value;
    }
  }
  return out;
}

function stringifyCart(cart) {
  return JSON.stringify(cartToPlain(cart, []));
}
import { loadScript } from 'beebee-base/lib/utility/load-script';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — application shell state: cart (tracked + sessionStorage
 *              mirror), locale/theme, storefront dashboard load, contact/save helpers
 *              shared by the whole app (saveRecord/destroyRecord/saveRecordRelated).
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        ref, lang, date, to_date, level, instr_lang, location, dispo, spec, age, q,
 *              status, stagione, theme, access_token
 *   key:       loadDataTask (loads /ecommerce_dashboard, parses per-language custom
 *              fields), loadMe, logoutTask, switchLangTask, sendContactMailTask,
 *              loadHereMapTask, calculateLocaleTask, addCart/delCart (@action, called
 *              unbound from child controllers/components), emptyCart, saveRecord,
 *              saveRecordRelated, destroyRecord, syncroRelation, tmpSaveLastSavedRecord.
 *   uses:      beebee-base/lib/utility (clearAttribute, notifyUserError), lib/utility/ajax
 *              (beebeeAjaxPost/Get), lib/get-config, lib/utility/load-script
 *   siblings:  routes/application.js (setupController seeds `cart` from sessionStorage,
 *              wires popper/body-click delegation)
 *   tests:     application-cart-persistence-test.js, application-getters-test.js,
 *              application-record-ops-test.js, application-tasks-test.js
 *   gotchas:   addCart/delCart MUST stay @action — templates/components invoke them
 *              unbound as this.args.delCart(...), and an unbound method runs with
 *              `this` = the frozen Glimmer args proxy (Sentry EMBER-SCUOLE-ECOMMERCE-2XA/2XB).
 *              emptyCart clears sessionStorage FIRST, then the tracked cart — routes/
 *              application.js re-seeds the controller from sessionStorage on every boot,
 *              so clearing only the tracked copy leaves paid-for items back in the cart
 *              on next load. saveRecord/saveRecordRelated never reject: they resolve
 *              `false` on a refused save (see project_resolve_false_dataloss memory).
 *
 * Root controller for the storefront shell (menu, cart drawer, locale switch,
 * dashboard product load) and the shared save/destroy record helpers every
 * other controller in this app calls through `applicationController`.
 */
export default class ApplicationController extends Controller {
  @iService intl
  @iService session
  @iService router
  @iService store
  @iService notify
  @iService log

  // Privacy-policy URL for the general-terms link in application.hbs. Octane migration
  // added this getter to checkin/checkout/payment-request controllers but missed the
  // application controller, leaving `href='{{this.generalTerms}}'` empty in the shell.
  get generalTerms() {
    return this.session.option_field?.ecommerce_general_term || null;
  }

  queryParams = ['ref', 'lang', 'date', 'to_date', 'level', 'instr_lang', 'location', 'dispo', 'spec', 'age', 'q', 'status', 'stagione', 'theme', 'access_token']
  /*lang = 'it-IT'*/

  urlData = getConfig(this).APP.urlData;

  @tracked instr_lang;
  @tracked location;
  @tracked dispo;
  @tracked lang;
  @tracked contactNameError = false;
  @tracked contactMailError = false;
  @tracked contactMessageError = false;
  @tracked noDispoOnline;
  @tracked me;

  @tracked content;
  @tracked urlApi;
  @tracked urlPdfApi;
  @tracked urlSocket;
  @tracked urlFileUpload;

  @tracked showCanvasAccount = false;
  @tracked showCanvasNotification = false;
  @tracked showCanvasMobileMenu = false;

  @tracked contact_name;
  @tracked contact_mail;
  @tracked contact_mobile;
  @tracked contact_message;

  @tracked cart = {
    groups: [],
    private: [],
    voucher: []
  }

  // Funzione che genera la lista di classi basata sui parametri attuali
  get queryParamClasses() {
    const params = this.queryParams;
    const classes = [];

    params.forEach((key) => {
      const value = this[key];
      if (value) {
        // Sanifica la stringa per CSS
        const safeValue = String(value)
          .replace(/[^a-zA-Z0-9_-]/g, '-')
          .toLowerCase();
        classes.push(`${key}-${safeValue}`);
      }
    });

    return classes.join(' ');
  }

  @action
  updateBodyClasses() {
    const appDiv = document.querySelector('body');
    if (!appDiv) return;

    // Rimuovi eventuali classi precedenti
    this.queryParams.forEach((key) => {
      appDiv.classList.forEach((cls) => {
        if (cls.startsWith(`${key}-`)) {
          appDiv.classList.remove(cls);
        }
      });
    });

    // Aggiungi le nuove
    this.queryParamClasses
      .split(' ')
      .filter(Boolean)
      .forEach((cls) => appDiv.classList.add(cls));
  }

  /**
   * Small-screen menu navigation. Closing the panel and transitioning here
   * keeps the rows as buttons: an <a href> that loses its click handler falls
   * back to a browser navigation, which reloads the whole app.
   */
  @action
  gotoMenu(route) {
    set(this, 'showMenu', null);
    this.router.transitionTo(route);
  }

  @action
  openCartFromMenu() {
    set(this, 'showMenu', null);
    set(this, 'showCart', true);
  }

  get currentVersion() {
    const version = getConfig(this).APP.version;

    if (version) {
      const [versionNumber, commit] = version.split('+');

      return {
        version: versionNumber,
        commit: commit,
      };
    }

    return null;
  }

  @computed('option_field.ecommerce_login_before')
  get ecommerceLoginBefore() {
    return this.option_field?.ecommerce_login_before ?? false;
  }

  get ecommerceShowBuy() {
    return !this.ecommerceLoginBefore || this.session.me?.id;
  }

  @computed('lang').readOnly()
  get langFlag() {
    if (!this.lang) {
      return;
    }

    const languageMap = {
      'fr-fr': 'francese',
      'en-us': 'inglese',
      'en-gb': 'inglese',
      'de-de': 'tedesco',
    };

    const lowercaseLang = this.lang.toLowerCase();
    return languageMap[lowercaseLang] || 'italiano';
  }

  @computed('date').readOnly()
  get dateMoment() {
    return this.date ? moment(this.date, 'YYYY-MM-DD') : null;
  }

  @computed('prodotti.file_private.file_url').readOnly()
  get imgPrivate() {
    if (!this.prodotti) {
      return null;
    }

    const fileUrl = get(this, 'prodotti.file_private.file_url');
    const defaultImageUrl = 'https://beebeeassets.beebeeboard.com/img/ecommerce-def-private.jpg';

    return fileUrl ? `${this.urlData}/${fileUrl}` : defaultImageUrl;
  }

  @computed('prodotti.file_corsi.file_url').readOnly()
  get imgCorsi() {
    if (!this.prodotti) {
      return null;
    }

    const fileUrl = get(this, 'prodotti.file_corsi.file_url');
    const defaultImageUrl = 'https://beebeeassets.beebeeboard.com/img/ecommerce-def-group.jpg';

    return fileUrl ? `${this.urlData}/${fileUrl}` : defaultImageUrl;
  }

  @computed('prodotti.file_gift.file_url').readOnly()
  get imgGift() {
    if (!this.prodotti) {
      return null;
    }

    const fileUrl = get(this, 'prodotti.file_gift.file_url');
    const defaultImageUrl = 'https://beebeeassets.beebeeboard.com/img/ecommerce-def-gift.jpg';

    return fileUrl ? `${this.urlData}/${fileUrl}` : defaultImageUrl;
  }

  get totCart() {
    const groupTotal = (this.cart.groups || []).reduce((acc, group) => acc + (group.prezzoAPI?.prezzo_preno || 0), 0);
    const privateTotal = (this.cart.private || []).reduce((acc, item) => acc + (item.prezzoAPI?.prezzo_preno || 0), 0);
    const voucherTotal = (this.cart.voucher || []).reduce((acc, voucher) => acc + (voucher.gift?.total || 0), 0);

    const num = (this.cart.groups?.length || 0) + (this.cart.private?.length || 0) + (this.cart.voucher?.length || 0);
    const total = groupTotal + privateTotal + voucherTotal;

    return {
      num,
      total
    };
  }

  @computed('session.option_field').readOnly()
  get option_field() {
     return this.session.option_field || null;
  }

  @computed('option_field.ecommerce_select_level').readOnly()
  get ecommerce_select_level() {
    const optionField = this.option_field;

    if (!optionField) {
      return true;
    }

    const ecommerceSelectLevel = optionField.ecommerce_select_level;
    return ecommerceSelectLevel !== undefined ? ecommerceSelectLevel : true;
  }

  @computed('option_field.ecommerce_private_hide_mail_info').readOnly()
  get showRichiediInfo() {
    const optionField = this.option_field;

    if (optionField?.ecommerce_private_hide_mail_info) {
      return optionField.ecommerce_private_hide_mail_info !== true;
    }

    return true;
  }

  @computed('option_field.ecommerce_theme', 'theme').readOnly()
  get getTheme() {
    const theme = this.theme || this.option_field?.ecommerce_theme;
    if (theme) {
      document.body.classList.add(theme);
      return theme;
    }

    return null;
  }

  @computed('content.open_organization.thumbnail_url').readOnly()
  get thumbnail_url() {
    const thumbnailUrl = get(this, 'content.open_organization.thumbnail_url');
    if (thumbnailUrl) {
      return `${this.urlData}/${thumbnailUrl}-medium.jpeg`;
    }
    return 'https://beebeeassets.beebeeboard.com/assets/img/logo.png';
  }

  @(task(function* (locales) {
    try {
      const response = yield fetch('https://ipapi.co/json');
      if (!response.ok) {
        // Existing try/catch below handles this via the catch(e) fallback.
        throw new Error(`HTTP ${response.status}`);
      }
      const dataIpApi = yield response.json();
      this.session.setIpLocation(dataIpApi);
      const langIp = dataIpApi?.languages?.split(',')[0];

      const language = langIp || navigator.languages[0] || navigator.language || navigator.userLanguage;

      return locales.includes(language.toLowerCase()) ? language : 'it-IT';
    } catch (e) {
      return 'it-IT';
    }
  }).restartable()) calculateLocaleTask

  @(task(function* (unloadEredirect = true) {
    this.showCanvasAccount = false;
    this.showCanvasNotification = false;
    this.showCanvasMobileMenu = false;

    // The API requires the token as a query parameter; the Authorization
    // header is sent as well for forward compatibility.
    yield beebeeAjaxGet(
      `${getConfig(this).APP.urlApi}/../logout?access_token=${this.session.token}`,
      this.session.token
    );

    if (this.socket) {
      this.socket.emit('logout', {
        user: this.content?.me?.id,
        token: this.session.token,
      });
    }

    this.session.logout();
    this.store.unloadAll();

    this.router.transitionTo('index');
    yield timeout(100);
    window.location.reload(true);
  }).restartable()) logoutTask

  @(task(function* () {
    try {
      set(this, 'invitationSent', false);
      set(this, 'mailError', null);

      const data = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/user_registration_request`,
        null,
        {
          data: {
            attributes: {
              mail: this.content?.me?.mail,
              contact_id: this.content?.me?.id,
            },
          },
        }
      );

      if (data && data.errors && data.errors[0].detail) {
        set(this, 'mailError', data.errors[0].detail);
      } else {
        set(this, 'invitationSent', true);
        this.notify.success(`${this.intl.t('message sent to')} ${this.content?.me?.mail}`);
      }
    } catch (e) {
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }).restartable()) sendInvitation

  @(task(function* () {
  try {
    yield timeout(100);

    let fromDate = this.date ? moment(this.date, 'YYYY-MM-DD') : null;

    if (this.option_field?.ecommerce_from_date) {
      const minFromDate = moment(this.option_field.ecommerce_from_date, 'YYYY-MM-DD');
      if (!fromDate || fromDate.isBefore(minFromDate)) {
        fromDate = minFromDate;
        set(this, 'date', fromDate.format('YYYY-MM-DD'));
      }
    }

    let toDate = this.to_date ? moment(this.to_date, 'YYYY-MM-DD') : null;

    if (fromDate && toDate && toDate.isBefore(fromDate)) {
      toDate = fromDate.clone();
      set(this, 'to_date', toDate.format('YYYY-MM-DD'));
    }

    const url = `${getConfig(this).APP.urlApi}/ecommerce_dashboard`;
    const accessToken = this.session.token;

    const params = {
      from_date: fromDate ? fromDate.format('YYYY-MM-DD') : null,
      to_date: toDate ? toDate.format('YYYY-MM-DD') : null,
      comp: this.location,
      spec: this.spec,
      livello: this.level,
      dispo: this.dispo,
      stagione: this.stagione,
      anni: this.age,
      status: this.status,
      q: this.q,
      access_token: accessToken
    };

    // The API requires the token as a query parameter; the Authorization
    // header is sent as well for forward compatibility.
    const response = yield beebeeAjaxGet(url, accessToken, params);

    // funzione di utilità per parsare i prodotti
    // One malformed JSON field must not kill the whole storefront load.
    const safeParse = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        this.log.warn('parseProduct: invalid JSON field', raw);
        return null;
      }
    };

    const parseProduct = (product) => {
      const cf = product.custom_fields || {};
      return {
        ...product,
        codice_json: safeParse(product.codice),
        modules_json: safeParse(product.modules),
        block_check_json: safeParse(product.block_check),
        // mappatura custom fields per lingua
        titleENG: cf['312']?.value ?? null,
        titleFRA: cf['313']?.value ?? null,
        titleDEU: cf['2002']?.value ?? null,
        ecommerceDescription: cf['303']?.value ?? null,
        ecommerceDescriptionENG: cf['311']?.value ?? null,
        ecommerceDescriptionFRA: cf['314']?.value ?? null,
        ecommerceDescriptionDEU: cf['2001']?.value ?? null,
        timeslot: cf['321']?.value ?? null,
        etminima: cf['218']?.value ?? null,
        etmassima: cf['219']?.value ?? null,
        totaleore: cf['320']?.value ?? null,
        meetingPoint: cf['455']?.value ?? null,
        skiResort: cf['227']?.value ?? null,
      };
    };

    if (Array.isArray(response?.data?.products)) {
      response.data.products = response.data.products.map(parseProduct);
    }

    if (Array.isArray(response?.data?.all_products)) {
      response.data.all_products = response.data.all_products.map(parseProduct);

      if (Array.isArray(response.data.vetrina)) {
        response.data.vetrina = response.data.vetrina.map(item =>
          response.data.all_products.find((p) => p.id === item.id) || item
        );
      }

      if (Array.isArray(response.data.popular)) {
        response.data.popular = response.data.popular.map(item =>
          response.data.all_products.find((p) => p.id === item.id) || item
        );
      }
    } else if (response?.data) {
      response.data.vetrina = [];
      response.data.popular = [];
    }

    set(this, 'prodotti', response?.data || {});
  } catch (e) {
    // Storefront dashboard load (4xx/network) was left uncaught and surfaced
    // as a Sentry unhandled rejection. Background loader: log only, no notify.
    this.log.warn('loadDataTask: /ecommerce_dashboard failed', e);
  }
}).restartable()) loadDataTask

  @(task(function* (lang = null) {
    const locales = ['en-us', 'en-gb', 'it-it', 'fr-fr', 'de-de'];

    yield timeout(50)

    let locale = lang.toLowerCase();
    if(!locale || !locales.includes(locale.toLowerCase())) {
      locale = yield this.calculateLocaleTask.perform(locales);
    }

    yield this.intl.loadTranslations(locale)
    this.intl.setLocale(locale)
    moment.locale(locale)

    set(this, 'lang', locale)

    if (this.zfLang) {
      this.zfLang.close();
    }

    set(this, 'showMenu', false);
    set(this, 'changeLang', false);
  }).restartable()) switchLangTask

  @(task(function* () {
      yield timeout(200);

      this.contactNameError = false;
      this.contactMailError = false;
      this.contactMessageError = false;

      const {
        contact_name,
        contact_mail,
        contact_mobile,
        contact_message,
        // The start-date filter lives in the 'date' query param; there is no
        // from_date property on this controller.
        date: from_date,
        to_date
      } = this;

      if (!contact_name || contact_name === '') {
        this.contactNameError = true;
        return false;
      }

      if (!contact_mail || !this.validateEmail(contact_mail)) {
        this.contactMailError = true;
        return false;
      }

      if (!contact_message || contact_message === '') {
        this.contactMessageError = true;
        return false;
      }

      try {
        yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/send_admin_mail`,
          this.session.token,
          {
            contact_name,
            contact_mail,
            contact_mobile,
            product_name: this.noDispoOnline,
            from_date: from_date ? moment(from_date, 'YYYY-MM-DD').format('DD/MM/YYYY') : null,
            to_date: to_date ? moment(to_date, 'YYYY-MM-DD').format('DD/MM/YYYY') : null,
            contact_message,
            thumbnail_url: this.content?.open_organization?.thumbnail_url,
            color: this.content?.open_organization?.primary_color,
            lang: this.intl.primaryLocale,
          }
        );

        this.noDispoOnline = false;
        this.contact_name = '';
        this.contact_mail = '';
        this.contact_mobile = '';
        this.contact_message = '';
        this.notify.success(
          this.intl.t('request sent correctly to')
        );
      } catch (e) {
        this.notify.error(
          this.intl.t('Error sending message')
        );
      }
    }).restartable()) sendContactMailTask

  @(task(function* () {
    yield timeout(150);

    const promises = [];
    //let appID = 'rJCaRrZvEWJ5ygWKqPrG';
    // HERE Maps API key: prefer per-app config (ENV.APP.here.apiKey); the
    // literal fallback keeps apps without that config entry working.
    const apiKey =
      getConfig(this).APP?.here?.apiKey ??
      'xclBSDj4_2_MroIvNErLrbdRGh9kMT2bu4BQ-TDPsKk';
    let Hereplatform = this.Hereplatform;
    const baseAssetUrl = 'https://js.api.here.com/v3';
    const shedulerVersion = '3.1';
    const arrFiles = [
      'mapsjs-ui.js',
      'mapsjs-service.js',
      'mapsjs-mapevents.js',
      'mapsjs-clustering.js',
    ];

    if (typeof H === 'undefined') {
      const link = document.createElement('link');
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('href', `https://js.api.here.com/v3/3.1/mapsjs-ui.css`);
      document.head.appendChild(link);

      yield loadScript(
        `${baseAssetUrl}/${shedulerVersion}/mapsjs-core.js`,
      );

      for (let i = 0; i < arrFiles.length; i++) {
        promises.push(
          loadScript(`${baseAssetUrl}/${shedulerVersion}/${arrFiles[i]}`),
        );
      }
      yield all(promises);
      yield timeout(150);

      Hereplatform = new H.service.Platform({
        apikey: apiKey,
      });
    } else if (H.a !== apiKey) {
      for (let i = 0; i < arrFiles.length; i++) {
        promises.push(
          loadScript(`${baseAssetUrl}/${shedulerVersion}/${arrFiles[i]}`),
        );
      }
      yield all(promises);
      yield timeout(150);

      Hereplatform = new H.service.Platform({
        apikey: apiKey,
      });
    }

    if (Hereplatform) {
      set(this, 'Hereplatform', Hereplatform);
    }
  }).restartable()) loadHereMapTask

  validateEmail(email) {
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  @(task(function* (result, save = true) {
    yield timeout(200);
    set(this, `${save ? 'operationSave' : 'operationDestroy'}`, result);
  }).restartable()) setOperationSaveTask

    @(task(function* (lang = null) {
      try {
        const response = yield beebeeAjaxGet(
          `${getConfig(this).APP.urlApi}/contacts/me`,
          this.session.token
        );

        // A failed/empty /contacts/me (401, network, "Not found") resolves to
        // undefined here; reading response.data.id then threw
        // (Sentry EMBER-SCUOLE-ECOMMERCE-2XM) and the unhandled rejection
        // surfaced as 2XJ. Guard the payload before touching it.
        const meId = response?.data?.id;
        if (!meId) {
          return;
        }

        this.store.pushPayload(response);
        const contactStore = this.store.peekRecord('contact', meId);

        this.me = contactStore;
        this.content.me = contactStore;
      } catch (e) {
        this.log.warn('loadMe: /contacts/me failed', e);
      }
    }).restartable()) loadMe

    // @action is REQUIRED here: templates pass these as {{this.addCart}} /
    // {{this.delCart}} and components invoke them via this.args.delCart(...),
    // so an unbound method runs with `this` = the frozen Glimmer args proxy
    // and `this.cart = ...` throws "Cannot redefine property: cart"
    // (Sentry EMBER-SCUOLE-ECOMMERCE-2XA/2XB).
    @action
    addCart(category = 'groups', product) {
      const current = Array.isArray(this.cart[category]) ? this.cart[category] : [];
      this.cart = { ...this.cart, [category]: [...current, product] };
      try {
        sessionStorage.setItem('beebeeEcommerceCart', stringifyCart(this.cart));
      } catch (e) {
        // stringifyCart already strips cycles; a throw here is storage-side
        // (private-mode quota), so the cart simply is not persisted this time.
        this.log.warn('addCart: cart not persisted to sessionStorage', e);
      }
      this.notify.success(this.intl.t('product add to cart'));

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'add_to_cart',
          product: category,
          ecommerce: {
            items: [product]
          }
        });
      }

      set(this, 'showCart', true);
    }

    @action
    delCart(category = 'groups', product, appC = null) {
      const controller = appC || this;
      const current = Array.isArray(controller.cart[category]) ? controller.cart[category] : [];
      controller.cart = { ...controller.cart, [category]: current.filter((p) => p !== product) };
      try {
        sessionStorage.setItem('beebeeEcommerceCart', stringifyCart(controller.cart));
      } catch (e) {
        // stringifyCart already strips cycles; a throw here is storage-side
        // (private-mode quota), so the cart simply is not persisted this time.
        controller.log.warn('delCart: cart not persisted to sessionStorage', e);
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'remove_from_cart',
          product: category,
          ecommerce: {
            items: [product]
          }
        });
      }

      controller.notify.success(controller.intl.t('product del to cart'));
    }

  // The cart lives in TWO places: this tracked object (what the screen shows)
  // and sessionStorage (what survives a reload). `app/routes/application.js`
  // setupController re-seeds the controller from sessionStorage on every boot,
  // so clearing only the tracked copy does not empty the cart — it hides it
  // until the next page load.
  //
  // This runs after an order completes (checkout.js `clearItems`, called by
  // <PaymentMethod> once the provider confirms, and `confermaPrezzo0` for a
  // zero-price order) and behind the "Vuota carrello ed effettua una nuova
  // prenotazione" control on the order-complete screen. Leaving the storage
  // copy behind put the lessons the customer had just PAID FOR back in their
  // cart on the next boot, priced and checkout-ready, with nothing on screen
  // saying they were already bought.
  //
  // Storage is cleared FIRST: if setItem throws (private-mode quota) the tracked
  // cart is still emptied, so the customer never sees a basket that the storage
  // has already dropped, and vice versa the screen is never emptied while the
  // storage copy is still waiting to resurrect.
  emptyCart() {
    const empty = {
      groups: [],
      private: [],
      voucher: []
    };

    try {
      sessionStorage.setItem('beebeeEcommerceCart', JSON.stringify(empty));
    } catch (e) {
      this.log.warn('emptyCart: cart not cleared from sessionStorage', e);
    }

    set(this, 'cart', empty);
  }

  findError(reason) {
    this.log.error('findError', reason);
  }

  tmpSaveLastSavedRecord(record, result = null, operation = 'update') {
    const modelName = record?.constructor?.modelName;
    if (!modelName || !record || record.isDeleted || record.isDeleting) {
      return;
    }

    const lastSavedRecord = this.lastSavedRecord || {};
    const operationData = lastSavedRecord[operation] || {};
    const modelArray = operationData[modelName] || [];

    const id = record.id ? parseInt(record.id) : result?.[0]?.data?.id || null;
    if (!id) return;

    const timestamp = Date.now();
    const updatedData = { id, timestamp };

    set(this, 'lastSavedRecord', {
      ...lastSavedRecord,
      [operation]: {
        ...operationData,
        [modelName]: [...modelArray, updatedData].slice(-5),
      },
    });
  }

  saveRecordRelated(record, notifyUser = true) {
    if (record) {
      if (record.option_json) {
        set(record, 'option', JSON.stringify(record.option_json));
      }
      if (record.modules_json) {
        set(record, 'modules', JSON.stringify(record.modules_json));
      }
    }

    return all([record.saveAll(record)]).then(
      (result) => {
        result.forEach((itemAPI) => {
          if (!itemAPI?.data?.id || !itemAPI?.data?.type) {
            this.log.warn('Payload non valido', itemAPI);
            return;
          }

          const { id, type } = itemAPI.data;
          const modelName = singularize(type);

          // Cerca record esistente nello store
          const existing = this.store.peekRecord(modelName, id);
          if (existing) {
            existing.rollbackAttributes();
          }

          this.store.pushPayload(itemAPI);
        });

        if (notifyUser) {
          this.notify.success(this.intl.t('save done'));
        }

        this.tmpSaveLastSavedRecord(record, result);
        this.setOperationSaveTask.perform(result);

        return result;
      },

      (reason) => {
        notifyUserError(this.notify, reason, this.intl);
        return false;
      }
    );
  }

  syncroRelation(item, notifyUser = true) {
    var self = this,
      daAggiungere = [],
      daRimuovere = [];

    if (item.addedRelated) {
      item.addedRelated.forEach(function(r) {
        var index = -1;
        for (let i = 0; i < daAggiungere.length; i++) {
          if (daAggiungere[i].model === r.get('constructor.modelName')) {
            index = i;
          }
        }

        if (index === -1) {
          daAggiungere.push({
            model: underscore(r.get('constructor.modelName')),
            ids: [r.id],
          });
        } else {
          daAggiungere[index].ids.push(r.id);
        }
      });
    }

    if (item.removedRelated) {
      item.removedRelated.forEach(function(r) {
        var index = -1;
        for (let i = 0; i < daRimuovere.length; i++) {
          if (daRimuovere[i].model === r.get('constructor.modelName')) {
            index = i;
          }
        }

        if (index === -1) {
          daRimuovere.push({
            model: underscore(r.get('constructor.modelName')),
            ids: [r.id],
          });
        } else {
          daRimuovere[index].ids.push(r.id);
        }
      });
    }

    return beebeeAjaxPost(
      `${getConfig(this).APP.urlApi}/sync_relations`,
      self.session.token,
      {
        model: underscore(item.get('constructor.modelName')),
        id: item.id,
        add: daAggiungere,
        del: daRimuovere,
      }
    )
      .then(function() {
        if (notifyUser) {
          //set(message, 'visible', false);
          self.notify.success(self.intl.t('save done'));
        }

        set(item, 'addedRelated', []);
        set(item, 'removedRelated', []);
      })
      .catch(function(error) {
        notifyUserError(self.notify, error, self.intl);
      });
  }

  saveRecord(record, notifyUser = true) {
    var self = this,
      promises = [];

    if (!record || !record.eachAttribute) {
      return new Promise(function(resolve /*, reject*/ ) {
        resolve(record);
      });
    }

    record.eachAttribute(function(name, meta) {
      set(record, name, clearAttribute(record.get(name)));
    });

    if (record.hasDirtyAttributes) {
      promises.push(record.save());
    }

    if (record.id && (record.addedRelated || record.removedRelated)) {
      promises.push(this.syncroRelation(record, false));
    }

    if (record.custom_fields) {
      promises.push(
        Promise.resolve(record.custom_fields).then(function(resolvedCF) {
          const cfSavePromises = [];
          resolvedCF.forEach(function(cf) {
            if (cf.hasDirtyAttributes) {
              cfSavePromises.push(cf.save());
            }
          });
          return all(cfSavePromises);
        })
      );
    }

    if (promises.length > 0) {
      /*if (notifyUser) {
                message = self.notify.success(self.intl.t('operation in progress...'), {
                    closeAfter: null
                });
            }*/

      return all(promises).then(
        function(array) {
          if (notifyUser) {
            //set(message, 'visible', false);
            self.notify.success(self.intl.t('save done'));
          }

          later(
            self,
            function() {
              set(self, 'operationSave', record);
            },
            200
          );
          return record;
        },
        function(reason) {
          /*if (notifyUser) {
              set(message, 'visible', false);
          }*/

          notifyUserError(self.notify, reason, self.intl);
          return false;
        }
      );
    }

    if (notifyUser) {
      later(
        self,
        function() {
          /*if (message) {
                    later(self, function() {
                        set(message, 'visible', false);
                    }, 150);
                }*/
          self.notify.success(self.intl.t('save done'));
        },
        500
      );
    }

    return new Promise(function(resolve /*, reject*/ ) {
      resolve(record);
    });
  }

  destroyRecord(record, redirect_url, notifyUser = true) {
    var self = this,
      id = record?.id;

    //set(this, 'applicationMessageHeader', 'Salvataggio in corso...');

    if (
      !record ||
      record.get('currentState.stateName') === 'root.deleted.saved'
    ) {
      if (notifyUser) {
        self.notify.success(self.intl.t('save done'));
      }

      set(self, 'operationDestroy', id);
      return new Promise(function (resolve /*, reject*/) {
        resolve(record);
      });
    } else if (!record.id) {
      if (notifyUser) {
        self.notify.success(self.intl.t('save done'));
      }

      set(self, 'operationDestroy', id);

      if (self.openCards) {
        self.openCards.forEach(function (f) {
          if (
            f.item &&
            f.item?.constructor?.modelName === record?.constructor?.modelName &&
            f.item.get('id') === id
          ) {
            self.closeCardTask.perform(f);
          }
        });
      }

      set(self, 'itemPreview', null);
      return record.deleteRecord();
    }

    /*if (notifyUser) {
            var message = self.notify.success(self.intl.t('operation in progress...'), {
                closeAfter: null
            });
        }*/

    return record.destroyRecord().then(
      function (retDestroy) {
        if (notifyUser) {
          //set(message, 'visible', false);
          self.notify.success(self.intl.t('save done'));
        }

        self.tmpSaveLastSavedRecord(record, retDestroy, 'destroy');

        set(self, 'itemPreview', null);

        /*self.openCards.forEach(function (f) {
          if (
            f.item &&
            f.item?.constructor?.modelName === record?.constructor?.modelName &&
            f.item.get('id') === id
          ) {
            self.applicationController.closeCardTask.perform(f);
          }
        });*/

        if (redirect_url) {
          self.router.transitionTo('list', redirect_url);
        }

        self.setOperationSaveTask.perform(id, false);

        return true;
      },
      function (reason) {
        /*if (notifyUser) {
                set(message, 'visible', false);
            }*/
        notifyUserError(self.notify, reason, self.intl);
        record.rollbackAttributes();
        //debug(reason);
        return false;
      },
    );
  }
}
